import { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import QRCode from "qrcode";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

type TicketView = {
  id: string;
  eventSlug: string;
  eventTitle: string;
  eventTitleZh: string;
  ticketTypeName: string;
  ticketTypeNameZh: string;
  ticketPriceHkd: number | null;
  fullName: string;
  displayName: string | null;
  email: string;
  phone: string;
  status: string;
  eTicketCode: string | null;
  approvedAt: string | null;
  checkedInAt: string | null;
  ticketUrl: string | null;
};

export default function TicketPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ticket, setTicket] = useState<TicketView | null>(null);
  const qrRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const load = async () => {
      if (!token) return;
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`/api/tickets/${encodeURIComponent(token)}`);
        const data = (await res.json()) as { ok?: boolean; ticket?: TicketView; error?: string };
        if (!res.ok || !data.ok || !data.ticket) {
          throw new Error(data.error || "failed");
        }
        setTicket(data.ticket);
      } catch {
        setError("Ticket not found or not approved yet.");
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [token]);

  // Generate QR code when ticket with eTicketCode is loaded
  useEffect(() => {
    if (!ticket?.eTicketCode || !qrRef.current) return;
    QRCode.toCanvas(qrRef.current, `AURAYS-CHECKIN:${ticket.eTicketCode}`, {
      width: 200,
      margin: 2,
      color: { dark: "#111827", light: "#F8F5FF" },
    }).catch(() => {
      // silent fail
    });
  }, [ticket?.eTicketCode]);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />

      <section className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          {loading ? (
            <div className="bg-white rounded-3xl border border-gray-100 p-8 text-gray-500">Loading e-ticket...</div>
          ) : !ticket ? (
            <div className="bg-white rounded-3xl border border-red-100 p-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-3">E-ticket unavailable</h1>
              <p className="text-sm text-red-600 mb-6">{error}</p>
              <Link
                to="/events"
                className="inline-flex items-center bg-gray-900 text-white font-semibold px-5 py-3 rounded-full hover:bg-gray-700 transition-colors"
              >
                Browse Events
              </Link>
            </div>
          ) : (
            <div className="bg-white rounded-[2rem] border border-gray-100 overflow-hidden shadow-sm">
              <div className="bg-gray-900 text-white px-8 py-8">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C8B6FF] mb-3">Aurays E-Ticket</p>
                <h1 className="text-3xl md:text-4xl font-bold">{ticket.eventTitleZh}</h1>
                <p className="text-sm text-gray-300 mt-2">{ticket.eventTitle}</p>
              </div>

              <div className="px-8 py-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  <div className="rounded-2xl bg-gray-50 p-5">
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Attendee</p>
                    <p className="text-lg font-semibold text-gray-900">{ticket.fullName}</p>
                    <p className="text-sm text-gray-500">{ticket.displayName || ticket.email}</p>
                  </div>
                  <div className="rounded-2xl bg-gray-50 p-5">
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Ticket Code</p>
                    <p className="text-2xl font-bold tracking-[0.18em] text-gray-900">{ticket.eTicketCode || "-"}</p>
                    <p className="text-sm text-gray-500 mt-1">{ticket.ticketTypeNameZh}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="rounded-2xl border border-gray-100 p-5">
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Status</p>
                    <p className="text-base font-semibold text-gray-900">
                      {ticket.checkedInAt ? "Checked In" : "Valid for Entry"}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-gray-100 p-5">
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Approved</p>
                    <p className="text-base font-semibold text-gray-900">
                      {ticket.approvedAt ? new Date(ticket.approvedAt).toLocaleString() : "-"}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-gray-100 p-5">
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Check-in</p>
                    <p className="text-base font-semibold text-gray-900">
                      {ticket.checkedInAt ? new Date(ticket.checkedInAt).toLocaleString() : "Not used yet"}
                    </p>
                  </div>
                </div>

                <div className="rounded-3xl border border-dashed border-[#C8B6FF] bg-[#F8F5FF] px-6 py-8 text-center mb-8">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#7C55CC] mb-4">
                    Show at Door / 出示進場
                  </p>
                  <div className="inline-flex items-center justify-center bg-white rounded-2xl p-4 shadow-sm mb-4">
                    <canvas ref={qrRef} className="w-[200px] h-[200px]" />
                  </div>
                  <p className="text-2xl font-bold tracking-[0.15em] text-[#5B3FA0] mb-2">
                    {ticket.eTicketCode || "-"}
                  </p>
                  <p className="text-sm text-[#5B3FA0] leading-relaxed">
                    Present this QR code together with a valid photo ID for check-in. / 請出示此 QR
                    碼及身份證明文件辦理入場登記。
                  </p>
                </div>

                <div className="text-sm text-gray-500 space-y-2">
                  <p>Email: {ticket.email}</p>
                  <p>Phone: {ticket.phone}</p>
                  <p>Ticket Type: {ticket.ticketTypeNameZh}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
